/**
 * Connect > Pricing manager at /connect/pricing.
 *
 * Searchable table of every partner price row. The five price columns
 * (first cost, FOB/delivered wholesale, FOB/delivered distributor) are
 * inline-editable and save per row via PATCH. "Import from file" parses a
 * pasted or uploaded CSV client-side, previews the diff (new / changed /
 * removed / duplicates), then applies it. A per-item History drawer shows the
 * price change log.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Save, Upload, History } from 'lucide-react';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { ClearableSearchInput } from '@/components/ClearableSearchInput';
import { gridTheadClassName } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { useSession } from '@/lib/useSession';
import { formatDateTime } from '@/lib/dateUtils';
import {
  hasConnectPricing,
  isNotAvailable,
  connectMoney,
  useConnectPrices,
  usePatchConnectPrice,
  useConnectPriceHistory,
  PRICE_FIELDS,
  type ConnectPrice,
  type PriceField,
} from '@/lib/useConnect';
import {
  AccessDeniedCard,
  ConnectHeader,
  NotAvailableCard,
  Modal,
  secondaryBtnCls,
} from './connectShared';
import { PriceImportFlow } from './PriceImport';

const FIELD_LABELS: Record<PriceField, string> = {
  first_cost: 'First cost',
  fob_wholesale: 'FOB wholesale',
  delivered_wholesale: 'Delivered wholesale',
  fob_distributor: 'FOB distributor',
  delivered_distributor: 'Delivered distributor',
};

export function ConnectPricing() {
  const { user } = useSession();
  // No more read/write levels: holding connect.pricing grants both.
  const canView = hasConnectPricing(user?.permissions, user?.superAdmin);
  const canWrite = canView;

  const [q, setQ] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [historyItem, setHistoryItem] = useState<string | null>(null);

  const pricesQ = useConnectPrices(q);

  if (user && !canView) return <AccessDeniedCard />;
  const backendMissing = isNotAvailable(pricesQ.error);

  const toolbar = (
    <div className="flex flex-wrap gap-3 items-end">
      <div className="flex-1 min-w-[220px]">
        <label htmlFor="search" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Search</label>
        <ClearableSearchInput
          id="search"
          value={q}
          onChange={setQ}
          placeholder="Item code, category, description..."
          className="w-full border border-amp-line rounded pl-3 py-1.5 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
        />
      </div>
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <ConnectHeader
        title="Pricing"
        subtitle="Partner price list across wholesale and distributor buckets. Edit a cell to change a price, or import a file to update in bulk."
        actions={
          <>
            <ToolPinToggle toolSlug="connect-pricing" toolName="Pricing" />
            {canWrite && (
              <button type="button" onClick={() => setImportOpen(true)} className={secondaryBtnCls}>
                <Upload className="h-3 w-3" /> Import from file
              </button>
            )}
          </>
        }
      />

      {backendMissing ? (
        <NotAvailableCard what="The pricing manager" />
      ) : (
        <DataGridShell
          total={pricesQ.count}
          loaded={pricesQ.prices.length}
          countNoun="price rows"
          toolbar={toolbar}
        >
          <table className="min-w-full text-sm">
            <thead className={gridTheadClassName}>
              <tr>
                <th className="px-3 py-2 text-left font-medium">Item</th>
                <th className="px-3 py-2 text-left font-medium w-[130px]">Category</th>
                {PRICE_FIELDS.map((f) => (
                  <th key={f} className="px-3 py-2 text-right font-medium w-[130px]">{FIELD_LABELS[f]}</th>
                ))}
                <th className="px-3 py-2 text-right font-medium w-[130px]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
              {pricesQ.isLoading ? (
                <tr><td colSpan={8} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading prices...</td></tr>
              ) : pricesQ.error ? (
                <tr><td colSpan={8} className="px-4 py-6 text-red-700 dark:text-red-300">Could not load: {pricesQ.error.message}</td></tr>
              ) : pricesQ.prices.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">No price rows match this search.</td></tr>
              ) : (
                pricesQ.prices.map((row) => (
                  <PriceRow key={row.item_code} row={row} canWrite={canWrite} onHistory={() => setHistoryItem(row.item_code)} />
                ))
              )}
            </tbody>
          </table>
        </DataGridShell>
      )}

      {importOpen && <PriceImportFlow onClose={() => setImportOpen(false)} />}
      {historyItem && <HistoryDrawer itemCode={historyItem} onClose={() => setHistoryItem(null)} />}
    </div>
  );
}

function PriceRow({ row, canWrite, onHistory }: { row: ConnectPrice; canWrite: boolean; onHistory: () => void }) {
  const patch = usePatchConnectPrice();
  const [draft, setDraft] = useState<Record<PriceField, string>>(() => {
    const d = {} as Record<PriceField, string>;
    for (const f of PRICE_FIELDS) d[f] = row[f] == null ? '' : String(row[f]);
    return d;
  });

  const dirty = PRICE_FIELDS.some((f) => draft[f] !== (row[f] == null ? '' : String(row[f])));

  const save = () => {
    const body: Partial<Record<PriceField, number | null>> = {};
    for (const f of PRICE_FIELDS) {
      const current = row[f] == null ? '' : String(row[f]);
      if (draft[f] !== current) {
        body[f] = draft[f] === '' ? null : Number(draft[f]);
      }
    }
    patch.mutate({ itemCode: row.item_code, body });
  };

  return (
    <tr className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
      <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">
        {/* Click-through to the product detail page; its back link returns
            here via ?from=pricing (Steven, 2026-07-06). */}
        <Link
          to={`/commerce/products/${encodeURIComponent(row.item_code)}?from=pricing`}
          className="font-mono text-xs underline decoration-dotted underline-offset-2 hover:text-amp-olive dark:hover:text-amp-olive-dark"
        >
          {row.item_code}
        </Link>
        {row.description && <div className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted truncate max-w-[240px]">{row.description}</div>}
      </td>
      <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted">{row.category ?? '--'}</td>
      {PRICE_FIELDS.map((f) => (
        <td key={f} className="px-3 py-2 text-right">
          {canWrite ? (
            <input
              type="number"
              step="0.01"
              value={draft[f]}
              onChange={(e) => setDraft((d) => ({ ...d, [f]: e.target.value }))}
              className="w-24 border border-amp-line rounded px-2 py-1 text-right text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
            />
          ) : (
            <span className="text-amp-ink dark:text-amp-dark-ink">{connectMoney(row[f])}</span>
          )}
        </td>
      ))}
      <td className="px-3 py-2 text-right">
        <div className="inline-flex items-center gap-2">
          {canWrite && (
            <button type="button" onClick={save} disabled={!dirty || patch.isPending} className="inline-flex items-center gap-1 text-xs border border-amp-line rounded px-2 py-1 text-amp-ink hover:bg-amp-cream-warm disabled:opacity-40 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm">
              {patch.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
            </button>
          )}
          <button type="button" onClick={onHistory} aria-label="Price history" title="Price history" className="text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink">
            <History className="h-3.5 w-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function HistoryDrawer({ itemCode, onClose }: { itemCode: string; onClose: () => void }) {
  const historyQ = useConnectPriceHistory(itemCode);
  const rows = historyQ.data?.history ?? [];

  return (
    <Modal title={`Price history: ${itemCode}`} onClose={onClose} wide>
      {historyQ.isLoading ? (
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading history...</p>
      ) : isNotAvailable(historyQ.error) ? (
        <NotAvailableCard what="Price history" />
      ) : historyQ.error ? (
        <p className="text-sm text-red-700 dark:text-red-300">{historyQ.error.message}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">No changes recorded for this item.</p>
      ) : (
        <table className="min-w-full text-sm">
          <thead className="text-amp-ink-muted dark:text-amp-dark-ink-muted">
            <tr>
              <th className="px-2 py-2 text-left font-medium">Field</th>
              <th className="px-2 py-2 text-right font-medium">Old</th>
              <th className="px-2 py-2 text-right font-medium">New</th>
              <th className="px-2 py-2 text-left font-medium">Changed by</th>
              <th className="px-2 py-2 text-left font-medium">When</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {rows.map((h, i) => (
              <tr key={i}>
                <td className="px-2 py-2 text-amp-ink dark:text-amp-dark-ink">{FIELD_LABELS[h.field as PriceField] ?? h.field}</td>
                <td className="px-2 py-2 text-right text-amp-ink-muted dark:text-amp-dark-ink-muted">{connectMoney(h.old_value)}</td>
                <td className="px-2 py-2 text-right text-amp-ink dark:text-amp-dark-ink">{connectMoney(h.new_value)}</td>
                <td className="px-2 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted">{h.changed_by ?? '--'}</td>
                <td className="px-2 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">{h.changed_at ? formatDateTime(h.changed_at) : '--'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}
